import React, { forwardRef, useState } from 'react'
import {
  Card,
  Text,
  Stack,
  Box,
  Heading,
  Button,
  Spinner,
  Flex,
  Badge,
} from '@sanity/ui'
import { ObjectInputProps, useFormValue, useClient } from 'sanity'

interface ScoredResult {
  fitmentTypeIndex: number
  fitmentTypeName: string
  listingIndex: number
  tireCount: number
  score: number
  isWinter: boolean
  wheelSpecs: string
}

interface ScoringResponse {
  success: boolean
  results: ScoredResult[]
  metadata: {
    maxTireCount: number
    benchmarkFitment: string
    benchmarkWheelSpecs: string
    totalListingsScored: number
    scoringCriteriaId: string
    /** true when the guide has a single fitment listing - every score is forced to 10 */
    singleFitment?: boolean
  }
}

const TireAvailabilityScoringButton = forwardRef<HTMLDivElement, ObjectInputProps>(
  (props, ref) => {
    // Get document ID using Sanity v4 hook
    const rawId = useFormValue(['_id']) as string | undefined
    const documentId = rawId?.replace('drafts.', '')
    
    // Get current dataset from Sanity client
    // In Sanity Studio v4, useClient() returns a client configured for the current workspace
    const client = useClient({ apiVersion: '2024-01-01' })
    // Access dataset from client config - try multiple possible ways
    const currentDataset = 
      (client as any).config?.()?.dataset || 
      (client as any).clientConfig?.dataset || 
      'preview'

    const [loading, setLoading] = useState(false)
    const [results, setResults] = useState<ScoringResponse | null>(null)
    const [error, setError] = useState('')
    const [lastAction, setLastAction] = useState<'preview' | 'save' | null>(null)

    const handleCalculateScores = async (dryRun: boolean = false) => {
      if (!documentId) {
        setError('Document must be saved first')
        return
      }

      setLoading(true)
      setError('')
      setResults(null)
      setLastAction(dryRun ? 'preview' : 'save')

      console.log('[TireScoring] Starting calculation for:', documentId, 'dryRun:', dryRun)

      const baseURL = process.env.SANITY_STUDIO_FE_URL || 'http://localhost:3000'
      try {
        const res = await fetch(`${baseURL}/api/scoring/tire-availability`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            buyerGuideId: documentId, 
            dryRun,
            dataset: currentDataset, // Pass current dataset to API
          }),
        })

        console.log('[TireScoring] Response status:', res.status)

        if (!res.ok) {
          const data = await res.json()
          console.error('[TireScoring] Error response:', data)
          throw new Error(data.message || 'Failed to calculate scores')
        }

        const data: ScoringResponse = await res.json()
        console.log('[TireScoring] Success:', data)
        setResults(data)
      } catch (err: any) {
        console.error('[TireScoring] Caught error:', err)
        if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
          setError(`Cannot connect to server. Make sure the frontend server is running at ${baseURL}`)
        } else {
          setError(err.message || 'Error calculating scores')
        }
      } finally {
        setLoading(false)
      }
    }

    const getScoreTone = (score: number): 'positive' | 'caution' | 'critical' => {
      if (score >= 7) return 'positive'
      if (score >= 4) return 'caution'
      return 'critical'
    }

    return (
      <Stack gap={4} ref={ref}>
        <Card padding={4} radius={2} shadow={1}>
          <Stack gap={3}>
            <Heading size={1}>Tire Availability Scoring</Heading>
            <Text size={1} muted>
              Calculate tire availability scores for all fitment listings. Scores are
              relative - the fitment with the most matching tires gets 10, others are
              scored based on their percentage reduction from the benchmark.
            </Text>
            <Text size={1} muted>
              Winter fitments have a score floor of 3 and ceiling of 8. Guides with a
              single fitment listing have no benchmark to compare against, so that
              listing always scores 10. Changes are saved as a draft for review before
              publishing.
            </Text>

            <Flex gap={2} wrap="wrap">
              <Button
                text={loading && lastAction === 'preview' ? 'Calculating...' : 'Preview Scores'}
                tone="default"
                onClick={() => handleCalculateScores(true)}
                disabled={loading || !documentId}
              />
              <Button
                text={loading && lastAction === 'save' ? 'Saving...' : 'Calculate & Save'}
                tone="primary"
                onClick={() => handleCalculateScores(false)}
                disabled={loading || !documentId}
              />
            </Flex>

            {!documentId && (
              <Text size={1} muted>
                Save the document first to calculate scores.
              </Text>
            )}
          </Stack>
        </Card>

        {error && (
          <Card padding={3} radius={2} tone="critical">
            <Text size={1}>{error}</Text>
          </Card>
        )}

        {loading && (
          <Card padding={4} radius={2}>
            <Flex align="center" justify="center" gap={3}>
              <Spinner />
              <Text size={1}>
                {lastAction === 'preview'
                  ? 'Calculating tire availability scores...'
                  : 'Calculating and saving scores...'}
              </Text>
            </Flex>
          </Card>
        )}

        {results && (
          <Card padding={4} radius={2} shadow={1}>
            <Stack gap={4}>
              <Flex align="center" justify="space-between" wrap="wrap" gap={2}>
                <Heading size={1}>
                  {lastAction === 'preview' ? 'Preview Results' : 'Saved Results'}
                </Heading>
                <Flex gap={2}>
                  <Badge tone={lastAction === 'save' ? 'positive' : 'caution'}>
                    {lastAction === 'save' ? 'Saved' : 'Preview Only'}
                  </Badge>
                  <Badge tone="default">
                    {results.metadata.totalListingsScored} listings
                  </Badge>
                </Flex>
              </Flex>

              {/* Benchmark Info */}
              {results.metadata.singleFitment ? (
                <Card padding={3} radius={2} tone="primary">
                  <Stack gap={2}>
                    <Text size={1} weight="semibold">
                      Single-fitment guide
                    </Text>
                    <Text size={1}>
                      There is no other fitment to compare against, so tire availability
                      is scored 10/10.
                    </Text>
                  </Stack>
                </Card>
              ) : (
                <Card padding={3} radius={2} tone="default">
                  <Stack gap={2}>
                    <Text size={1} weight="semibold">
                      Benchmark (Best Tire Availability)
                    </Text>
                    <Flex gap={4} wrap="wrap">
                      <Text size={1}>
                        <strong>Fitment:</strong> {results.metadata.benchmarkFitment}
                      </Text>
                      <Text size={1}>
                        <strong>Setup:</strong> {results.metadata.benchmarkWheelSpecs}
                      </Text>
                      <Text size={1}>
                        <strong>Tire Models:</strong> {results.metadata.maxTireCount}
                      </Text>
                    </Flex>
                  </Stack>
                </Card>
              )}

              {/* Results Table */}
              <Box
                style={{
                  maxHeight: '400px',
                  overflow: 'auto',
                }}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '13px',
                  }}
                >
                  <thead>
                    <tr style={{ position: 'sticky', top: 0, backgroundColor: 'var(--card-bg-color)' }}>
                      <th
                        style={{
                          padding: '10px 8px',
                          borderBottom: '2px solid var(--card-border-color)',
                          textAlign: 'left',
                        }}
                      >
                        Fitment Type
                      </th>
                      <th
                        style={{
                          padding: '10px 8px',
                          borderBottom: '2px solid var(--card-border-color)',
                          textAlign: 'left',
                        }}
                      >
                        Wheel Setup
                      </th>
                      <th
                        style={{
                          padding: '10px 8px',
                          borderBottom: '2px solid var(--card-border-color)',
                          textAlign: 'center',
                          width: '100px',
                        }}
                      >
                        Tire Models
                      </th>
                      <th
                        style={{
                          padding: '10px 8px',
                          borderBottom: '2px solid var(--card-border-color)',
                          textAlign: 'center',
                          width: '80px',
                        }}
                      >
                        Score
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.results.map((r, idx) => (
                      <tr key={idx}>
                        <td
                          style={{
                            padding: '8px',
                            borderBottom: '1px solid var(--card-border-color)',
                          }}
                        >
                          <Flex align="center" gap={2}>
                            <span>{r.fitmentTypeName}</span>
                            {r.isWinter && (
                              <Badge
                                tone="caution"
                                style={{ fontSize: '10px' }}
                              >
                                Winter
                              </Badge>
                            )}
                          </Flex>
                        </td>
                        <td
                          style={{
                            padding: '8px',
                            borderBottom: '1px solid var(--card-border-color)',
                            fontFamily: 'monospace',
                            fontSize: '12px',
                          }}
                        >
                          {r.wheelSpecs}
                        </td>
                        <td
                          style={{
                            padding: '8px',
                            borderBottom: '1px solid var(--card-border-color)',
                            textAlign: 'center',
                          }}
                        >
                          {r.tireCount}
                        </td>
                        <td
                          style={{
                            padding: '8px',
                            borderBottom: '1px solid var(--card-border-color)',
                            textAlign: 'center',
                          }}
                        >
                          <Badge tone={getScoreTone(r.score)}>
                            {r.score}/10
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>

              {/* Score Legend */}
              <Flex gap={3} wrap="wrap">
                <Text size={0} muted>
                  Score Legend:
                </Text>
                <Flex gap={2} align="center">
                  <Badge tone="positive">7-10</Badge>
                  <Text size={0} muted>Excellent</Text>
                </Flex>
                <Flex gap={2} align="center">
                  <Badge tone="caution">4-6</Badge>
                  <Text size={0} muted>Average</Text>
                </Flex>
                <Flex gap={2} align="center">
                  <Badge tone="critical">1-3</Badge>
                  <Text size={0} muted>Limited</Text>
                </Flex>
              </Flex>

              {lastAction === 'save' && (
                <Card padding={3} radius={2} tone="positive">
                  <Text size={1}>
                    Changes have been saved to a draft. Review and publish when ready.
                  </Text>
                </Card>
              )}
            </Stack>
          </Card>
        )}
      </Stack>
    )
  }
)

TireAvailabilityScoringButton.displayName = 'TireAvailabilityScoringButton'

export default TireAvailabilityScoringButton
